/**
 * AnimationClipUtils - utilities for filtering and processing animation clip tracks.
 */
import * as THREE from 'three';

export function collectValidNodeNames(model: THREE.Object3D): Set<string> {
  const validNodeNames = new Set<string>();
  model.traverse((node) => {
    validNodeNames.add(node.name);
  });
  return validNodeNames;
}

export function getTrackNodeName(trackName: string): string {
  // Handles standard names like "Hips.position", path names, and GLTF skin tracks.
  const dotIdx = trackName.indexOf('.');
  const targetPath = dotIdx >= 0 ? trackName.slice(0, dotIdx) : trackName;
  const boneMatch = targetPath.match(/\.bones\[(.+?)\]/);
  if (boneMatch && boneMatch[1]) {
    return boneMatch[1];
  }
  const pathParts = targetPath.split('/');
  return pathParts[pathParts.length - 1] ?? targetPath;
}

export function filterClipTracks(
  clip: THREE.AnimationClip,
  validNodeNames: Set<string>,
  includeBones?: Iterable<string>,
  excludeBones?: Iterable<string>
): THREE.AnimationClip {
  const includeSet = includeBones ? new Set(includeBones) : null;
  const excludeSet = excludeBones ? new Set(excludeBones) : null;

  const filteredTracks: THREE.KeyframeTrack[] = [];
  let tracksWereFiltered = false;

  for (const track of clip.tracks) {
    const nodeName = getTrackNodeName(track.name);
    if (!validNodeNames.has(nodeName)) {
      tracksWereFiltered = true;
      continue;
    }
    if (includeSet && includeSet.size > 0 && !includeSet.has(nodeName)) {
      tracksWereFiltered = true;
      continue;
    }
    if (excludeSet && excludeSet.has(nodeName)) {
      tracksWereFiltered = true;
      continue;
    }
    filteredTracks.push(track);
  }

  if (!tracksWereFiltered) {
    return clip;
  }

  return new THREE.AnimationClip(
    clip.name,
    clip.duration,
    filteredTracks,
    clip.blendMode
  );
}
